/**
 * Owns application navigation and the user's sidebar expansion preferences.
 * Reads trusted capability snapshots; never authorizes server operations or stores credentials.
 */
import { useEffect, useRef, useState } from 'react';
import { canManagePluginStore } from '../../../shared/domain/plugin-access.ts';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import upLabShip from '../assets/up-lab-ship.png';
import upMark from '../assets/up-mark-light.svg';
import type { AuthUser } from '../lib/api.ts';
import { gitlabTabHref, readGitlabTab } from '../lib/gitlab-navigation.ts';
import { useMobileNavigation } from './useMobileNavigation.ts';

/* 单色线性图标 —— 16px，stroke 跟随文字色 */
function Icon({ d }: { d: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const ICONS = {
  board: 'M2.5 2.5h4.5v4.5h-4.5zM9 2.5h4.5v4.5h-4.5zM2.5 9h4.5v4.5h-4.5zM9 9h4.5v4.5h-4.5z',
  decisions: 'M2.5 4h11M2.5 8h11M2.5 12h7',
  attribution: 'M8 3v3.5M8 6.5 4.5 9.5M8 6.5l3.5 3M4.5 9.5V13M11.5 9.5V13',
  repository: 'M2.5 3.25h11v9.5h-11zM2.5 5.5h11M6.5 7 5 8.5l1.5 1.5M9.5 7 11 8.5 9.5 10',
  usage: 'M2.5 13.5v-4M6.2 13.5v-7M9.9 13.5V3.5M13.5 13.5v-5.5',
  rules: 'M3 3.5h10M3 7h10M3 10.5h6M12 9.5l1 1 2-2.5',
  relays: 'M2 3h12v4H2zM2 9h12v4H2zM4 5h.1M4 11h.1M9 5h3M9 11h3M8 7v2',
  admin: 'M3 5h10M3 11h10M6 3v4M10 9v4',
  extensions: 'M2 3h4V1.5a1.5 1.5 0 0 1 3 0V3h4v4h1.5a1.5 1.5 0 0 1 0 3H13v4H9v-1.5a1.5 1.5 0 0 0-3 0V14H2v-4h1.5a1.5 1.5 0 0 0 0-3H2z',
  settings: 'M8 5.2A2.8 2.8 0 1 0 8 10.8 2.8 2.8 0 0 0 8 5.2zM8 1.5l.4 1.8 1.7-.7 1.3 1.3-.7 1.7 1.8.4v1.8l-1.8.4.7 1.7-1.3 1.3-1.7-.7-.4 1.8H7.6l-.4-1.8-1.7.7-1.3-1.3.7-1.7-1.8-.4V6l1.8-.4-.7-1.7L6.4 2.6l1.7.7z',
  password: 'M3.5 7.5h9v6h-9zM5.5 7.5v-2a2.5 2.5 0 0 1 5 0v2',
  manual: 'M3 2.5h7.5A2.5 2.5 0 0 1 13 5v8.5H5a2 2 0 0 1-2-2v-9zM3 11.5a2 2 0 0 1 2-2h8M6 5h4M6 7h3',
  signout: 'M6 2.5H3.5v11H6M10.5 5l3 3-3 3M13 8H6.5',
  collapse: 'M9.5 5 6 8.5 9.5 12M13.5 5 10 8.5l3.5 3.5',
  chevron: 'M5 6.2 8 9.2l3-3',
};

/* ── 侧栏导航模型 ─────────────────────────────────────────
 * 叶子项直接导航；组项点击只展开/收起（导航由子项承担），
 * 收起模式下多数分组点击直达默认页；模型和代码仓库先展示子菜单供用户选择。
 * 当前路由命中的组自动展开；手动开合记在 localStorage 里。
 */
interface NavChild {
  readonly to: string;
  readonly label: string;
  readonly active: boolean;
}

type NavEntry =
  | { readonly kind: 'leaf'; readonly key: string; readonly to: string; readonly label: string; readonly icon: string }
  | {
      readonly kind: 'group';
      readonly key: string;
      readonly to: string;
      readonly label: string;
      readonly icon: string;
      readonly match: readonly string[];
      readonly children: readonly NavChild[];
    };

function buildNav(
  perms: readonly string[],
  adminTab: string,
  pathname: string,
  hasUser: boolean,
  role: string | undefined,
  repositoryParams: URLSearchParams,
): readonly NavEntry[] {
  const isAdmin = role === 'admin';
  const list: NavEntry[] = isAdmin ? [] : [
    { kind: 'leaf', key: 'board', to: '/', label: '看板', icon: ICONS.board },
    { kind: 'leaf', key: 'decisions', to: '/decisions', label: '待决队列', icon: ICONS.decisions },
  ];
  if (!isAdmin && perms.includes('attribution:read')) {
    list.push({
      kind: 'group',
      key: 'attribution',
      to: '/attribution',
      label: '目标与成果',
      icon: ICONS.attribution,
      match: ['attribution', 'tasks', 'hypothesis'],
      children: [
        {
          to: '/attribution',
          label: '目标总览',
          active: pathname === '/attribution' || pathname.startsWith('/hypothesis/'),
        },
        { to: '/tasks', label: '任务概览', active: pathname.startsWith('/tasks') },
      ],
    });
  }
  if (perms.includes('rule:manage')) {
    list.push({ kind: 'leaf', key: 'rules', to: '/rules', label: '规则', icon: ICONS.rules });
  }
  if (perms.includes('relay:read')) {
    list.push({ kind: 'leaf', key: 'relays', to: '/relays', label: '中继服务', icon: ICONS.relays });
  }
  if (!isAdmin && hasUser) {
    const tab = readGitlabTab(repositoryParams.get('tab'), false);
    list.push({
      kind: 'group',
      key: 'repositories',
      to: gitlabTabHref('projects', repositoryParams),
      label: '代码仓库',
      icon: ICONS.repository,
      match: ['gitlab'],
      children: [
        { to: gitlabTabHref('projects', repositoryParams), label: '项目列表', active: pathname === '/gitlab' && tab === 'projects' },
        { to: gitlabTabHref('commits', repositoryParams), label: '我的提交', active: pathname === '/gitlab' && tab === 'commits' },
        { to: gitlabTabHref('connection', repositoryParams), label: '连接配置', active: pathname === '/gitlab' && tab === 'connection' },
      ],
    });
  }
  if (isAdmin && perms.includes('gitlab:audit')) {
    const tab = readGitlabTab(repositoryParams.get('tab'), true, true);
    list.push({
      kind: 'group', key: 'admin-repositories', to: '/gitlab', label: '代码仓库', icon: ICONS.repository,
      match: ['gitlab'],
      children: [
        { to: '/gitlab', label: '项目总览', active: pathname === '/gitlab' && tab === 'projects' },
        { to: '/gitlab?tab=commits', label: '提交记录', active: pathname === '/gitlab' && tab === 'commits' },
        { to: '/gitlab?tab=analytics', label: '提交统计', active: pathname === '/gitlab' && tab === 'analytics' },
        { to: '/gitlab?tab=users', label: 'GitLab 用户', active: pathname === '/gitlab' && tab === 'users' },
      ],
    });
  }
  if (isAdmin && perms.includes('usage:read')) {
    list.push({ kind: 'leaf', key: 'admin-usage', to: '/usage', label: '模型用量', icon: ICONS.usage });
  } else if (!isAdmin && (hasUser || perms.includes('usage:read'))) {
    list.push({
      kind: 'group',
      key: 'models',
      to: hasUser ? '/models' : '/usage',
      label: '模型',
      icon: ICONS.usage,
      match: ['models', 'usage'],
      children: [
        ...(hasUser ? [{ to: '/models', label: '我的模型', active: pathname === '/models' || pathname.startsWith('/models/') }] : []),
        ...(perms.includes('usage:read') ? [{ to: '/usage', label: '模型用量', active: pathname === '/usage' || pathname.startsWith('/usage/') }] : []),
      ],
    });
  }
  const canUsers = perms.includes('user:manage');
  const canRoles = perms.includes('role:manage');
  const canConfig = perms.includes('config:manage');
  if (isAdmin && canConfig) {
    list.push({ kind: 'leaf', key: 'sandbox', to: '/sandbox', label: '沙箱分析', icon: ICONS.admin });
  }
  const canPlugins = canManagePluginStore(perms, role);
  // 用户管理（账号/角色）与系统管理（运行参数）是两组：前者管人，后者管系统行为。
  if (canUsers || canRoles) {
    list.push({
      kind: 'group',
      key: 'admin',
      to: canUsers ? '/admin?tab=users' : '/admin?tab=roles',
      label: '用户管理',
      icon: ICONS.admin,
      match: ['admin'],
      children: [
        ...(canUsers ? [{
          // 两个子项路径相同（/admin），靠 ?tab= 区分 —— 所以激活判定必须自己算，
          // NavLink 的 isActive 只看路径，会把两个都标成当前页。
          to: '/admin?tab=users',
          label: '账号',
          active: pathname.startsWith('/admin') && adminTab !== 'roles',
        }] : []),
        ...(canRoles ? [{
          to: '/admin?tab=roles',
          label: '角色与权限',
          active: pathname.startsWith('/admin') && adminTab === 'roles',
        }] : []),
      ],
    });
  }
  if (canPlugins) {
    list.push({
      kind: 'group', key: 'extensions', to: '/extensions/dsh',
      label: '插件与技能', icon: ICONS.extensions, match: ['extensions'],
      children: [
        {to: '/extensions/dsh', label: '插件管理', active: pathname === '/extensions/dsh'},
        {to: '/extensions/skills', label: 'Skill 管理', active: pathname === '/extensions/skills'},
      ],
    });
  }
  if (canConfig) {
    // 设置八个域平铺为子项（2026-09-23 要求）：每域一个真实路由，激活按精确路径
    // 判定 —— startsWith 会让「通用」在所有 /settings/* 下都亮着。
    list.push({
      kind: 'group',
      key: 'settings',
      to: '/settings/general',
      label: '系统管理',
      icon: ICONS.settings,
      match: ['settings'],
      children: SETTINGS_NAV.map((s) => ({
        to: `/settings/${s.key}`,
        label: s.title,
        active: pathname === `/settings/${s.key}`,
      })),
    });
  }
  if (hasUser) {
    list.push({ kind: 'leaf', key: 'manual', to: '/manual', label: '使用手册', icon: ICONS.manual });
  }
  return list;
}

/** 设置域导航（与 /settings 子路由一一对应；侧栏是这几个页的唯一导航）。 */
const SETTINGS_NAV: readonly { key: string; title: string }[] = [
  { key: 'general', title: '通用' },
  { key: 'signals', title: '摩擦与信号' },
  { key: 'agent', title: 'Agent 分派' },
  { key: 'jobs', title: '任务调度' },
  { key: 'feishu', title: '飞书集成' },
  { key: 'gateway', title: '模型中转站' },
  { key: 'gitlab', title: '代码仓库' },
  { key: 'tos', title: '对象存储' },
  { key: 'attachments', title: '附件管理' },
  { key: 'evalmodel', title: '验收评审模型' },
  { key: 'sso', title: '单点登录' },
];

/* localStorage 在隐私模式下会直接抛异常，而「上次展开哪一组 / 上次收起没有」
   只是使用习惯，不值当让整个界面崩掉。 */
const SIDE_KEY = 'uplab_side';
const OPEN_KEY = 'uplab_side_open';

function readStore(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStore(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 隐私模式，忽略 */
  }
}

interface SidebarProps {
  readonly perms: readonly string[];
  readonly me: AuthUser | null;
  readonly onSignOut: () => void;
  readonly onPassword: () => void;
  /** 打开个人中心的登录设备页（/sessions）。 */
  readonly onSessions: () => void;
}

/**
 * 侧栏：208px 展开 / 48px 收起的图标栏，两级菜单（分组 → 页）。
 *
 * 规范对齐 `morecut-server/frontend/src/layouts/ConsoleSider.tsx`：
 * 收起态的二级菜单变成 hover 浮层、单开手风琴、窄屏自动收起但不写回偏好。
 * 那套实现借 antd Menu 的 `inlineCollapsed`，这里是零依赖等价物 ——
 * 行为照搬，DOM 与 CSS 自己写（本项目没有组件库）。
 */
export function Sidebar({ perms, me, onSignOut, onPassword, onSessions }: SidebarProps) {
  const location = useLocation();
  const { isMobile, mobileOpen, setOpen, closeNavigation, navigationRef, triggerRef } = useMobileNavigation(location.key);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedAdminTab = searchParams.get('tab');
  const adminTab = requestedAdminTab === 'roles' && perms.includes('role:manage')
    ? 'roles'
    : requestedAdminTab === 'users' && perms.includes('user:manage')
      ? 'users'
      : perms.includes('user:manage') ? 'users' : 'roles';
  // 审计与个人凭证沿用原有权限；仓库子页共享所选账户等 URL 状态。
  const repositoryParams = location.pathname === '/gitlab' ? searchParams : new URLSearchParams();
  const nav = buildNav(perms, adminTab, location.pathname, me !== null, me?.role, repositoryParams);

  // 窄屏自动收起 vs 用户手动收起：两个独立的量。窄屏收起不覆盖用户偏好，
  // 用户手动展开也不在窄屏下失效（他可能就是要在窄屏下看全菜单）。
  const [autoCollapsed, setAutoCollapsed] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState(() => readStore(SIDE_KEY) === '1');
  const collapsed = !isMobile && (userCollapsed || autoCollapsed);
  const toggleCollapsed = (): void => {
    if (isMobile) {
      closeNavigation();
      return;
    }
    const next = !collapsed;
    setUserCollapsed(next);
    setAutoCollapsed(false);
    writeStore(SIDE_KEY, next ? '1' : '0');
  };
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    setAutoCollapsed(mq.matches);
    const onChange = (e: MediaQueryListEvent): void => setAutoCollapsed(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // 取路径第一段而不是整条路径：详情页地址是 /hypothesis/xxx，
  // 用整条去比菜单键一个都匹配不上，侧栏会变成"没有任何项高亮"。
  const pathSeg = location.pathname.split('/')[1] || '';
  const currentGroup = nav.find((n) => n.kind === 'group' && n.match.includes(pathSeg))?.key;

  // 单开手风琴：openGroup 是单个组名（不是集合）。当前路由所在组自动展开 ——
  // 必须用 ref 记住「上次自动展开过谁」，否则手动展开别的组会被
  // 「路由还在旧组」的自动逻辑立刻撤销（点了没反应，不报错不动画）。
  const [openGroup, setOpenGroup] = useState<string>(() => readStore(OPEN_KEY) ?? '');
  const lastAutoGroup = useRef('');
  useEffect(() => {
    if (currentGroup !== undefined && lastAutoGroup.current !== currentGroup) {
      lastAutoGroup.current = currentGroup;
      setOpenGroup(currentGroup);
      writeStore(OPEN_KEY, currentGroup);
    }
  }, [currentGroup]);
  const onParentToggle = (key: string): void => {
    const next = openGroup === key ? '' : key;
    setOpenGroup(next);
    writeStore(OPEN_KEY, next);
  };

  const [focusedChoiceGroup, setFocusedChoiceGroup] = useState('');

  /* 用户菜单：点用户面板弹出（修改密码 / 退出登录），点外部或按 Esc 关闭。 */
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const footRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: PointerEvent): void => {
      if (footRef.current && !footRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  return (
    <>
    <header className="mobile-topbar">
      <div className="mobile-brand">
        <img className="brand-mark" src={upMark} alt="" />
        <span className="brand-text">UP-LAB</span>
      </div>
      <button
        ref={triggerRef}
        type="button"
        className="mobile-menu-toggle"
        aria-label="打开导航菜单"
        aria-expanded={mobileOpen}
        aria-controls="app-navigation"
        onClick={() => setOpen(true)}
      >
        <Icon d="M2.5 4h11M2.5 8h11M2.5 12h11" />
        <span>菜单</span>
      </button>
    </header>
    {mobileOpen && <button type="button" className="mobile-nav-mask" tabIndex={-1} aria-label="关闭导航遮罩" onClick={closeNavigation} />}
    <aside
      ref={navigationRef}
      id="app-navigation"
      className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}
      role={mobileOpen ? 'dialog' : undefined}
      aria-modal={mobileOpen ? true : undefined}
      aria-label="主导航"
      aria-hidden={isMobile && !mobileOpen ? true : undefined}
      inert={isMobile && !mobileOpen ? true : undefined}
      onClickCapture={(event) => {
        if (isMobile && (event.target as HTMLElement).closest('a[href], .user-menu [role="menuitem"]')) closeNavigation();
      }}
    >
      <div className="side-brand">
        <img
          className="brand-mark"
          src={collapsed ? upMark : upLabShip}
          alt={collapsed ? 'UP-LAB' : ''}
        />
        <span className="brand-text">UP-LAB</span>
        {/* 收起态的 48px 栏里，品牌与这个按钮竖排（并排放不下，会叠在一起）——
            品牌标识任何时候都在，控制按钮紧随其后 */}
        <button
          type="button"
          className="side-collapse"
          onClick={toggleCollapsed}
          aria-label={isMobile ? '关闭导航菜单' : collapsed ? '展开菜单' : '收起菜单'}
          data-label={isMobile ? undefined : collapsed ? '展开菜单' : '收起菜单'}
        >
          <Icon d={isMobile ? 'M4 4l8 8M12 4l-8 8' : ICONS.collapse} />
        </button>
      </div>

      <nav className="side-nav">
        {nav.map((item) => {
          if (item.kind === 'leaf') {
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === '/'}
                target={item.key === 'manual' ? '_blank' : undefined}
                rel={item.key === 'manual' ? 'noopener noreferrer' : undefined}
                className={({ isActive }) => (isActive ? 'on' : '')}
                // 收起态标签文字 display:none 会一起从无障碍树里消失，链接就没名字了
                aria-label={item.key === 'manual' ? '使用手册（新标签页打开）' : collapsed ? item.label : undefined}
                data-label={collapsed ? item.label : undefined}
                title={item.key === 'manual' ? '在新标签页打开' : undefined}
              >
                <Icon d={item.icon} />
                <span className="nav-label">{item.label}{item.key === 'manual' && <span aria-hidden="true"> ↗</span>}</span>
              </NavLink>
            );
          }
          const open = openGroup === item.key;
          const subId = `nav-sub-${item.key}`;
          const choiceGroup = item.key === 'models' || item.key === 'repositories' || item.key === 'admin-repositories';
          const choiceFlyout = collapsed && choiceGroup;
          return (
            // open 类是手风琴的开关（子菜单高度 0fr → 1fr + chevron 转向）。
            // 上一版漏了这个类：子菜单恒展开、chevron 恒指右，两个组同时铺开。
            <div
              className={`nav-group${open && !collapsed ? ' open' : ''}`}
              key={item.key}
              onFocusCapture={() => { if (choiceGroup) setFocusedChoiceGroup(item.key); }}
              onBlurCapture={(event) => {
                if (choiceGroup && !event.currentTarget.contains(event.relatedTarget)) setFocusedChoiceGroup('');
              }}
            >
              <button
                type="button"
                className={`nav-parent${currentGroup === item.key ? ' on' : ''}`}
                aria-expanded={choiceFlyout ? focusedChoiceGroup === item.key : collapsed ? undefined : open}
                aria-controls={collapsed && !choiceFlyout ? undefined : subId}
                aria-haspopup={collapsed ? true : undefined}
                aria-label={collapsed ? item.label : undefined}
                onClick={(event) => {
                  if (choiceFlyout) event.currentTarget.focus();
                  else if (collapsed) navigate(item.to);
                  else onParentToggle(item.key);
                }}
              >
                <Icon d={item.icon} />
                <span className="nav-label">{item.label}</span>
                <span className="nav-chevron">
                  <Icon d={ICONS.chevron} />
                </span>
              </button>
              {/* 收起态渲染它才有 hover 浮层可弹（此时 CSS 把它整个移出文档流，
                  展开态则靠 open 类做 0fr → 1fr 的高度过渡）。
                  关合的展开态用 inert 把里头链接从焦点顺序里摘出去。 */}
              <div
                className="nav-sub-wrap"
                id={subId}
                inert={!collapsed && !open ? true : undefined}
              >
                <div className="nav-sub">
                  {item.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      className={c.active ? 'on' : ''}
                      aria-current={c.active ? 'page' : undefined}
                      onClick={(event) => {
                        if (choiceFlyout && event.detail > 0) event.currentTarget.blur();
                      }}
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <div className="side-foot" ref={footRef}>
        {me !== null && (
          <>
            <button
              type="button"
              className={`side-user-btn${userMenuOpen ? ' on' : ''}`}
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
              aria-label={`${me.displayName}菜单`}
              onClick={() => setUserMenuOpen((v) => !v)}
            >
              <span className="avatar">{me.displayName.slice(0, 1)}</span>
              <span className="who">
                <b>{me.displayName}</b>
                <small>{me.role}</small>
              </span>
              <span className="user-menu-chevron">
                <Icon d={ICONS.chevron} />
              </span>
            </button>
            {userMenuOpen && (
              <div className="user-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    onPassword();
                  }}
                  disabled={me.isSso}
                  title={
                    me.isSso
                      ? '统一身份账号的密码在公司统一身份平台管理'
                      : undefined
                  }
                >
                  <Icon d={ICONS.password} />
                  <span>修改密码</span>
                </button>
                <button type="button" role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onSessions(); }}
                >
                  <Icon d={ICONS.admin} />
                  <span>登录设备</span>
                </button>
                <button type="button" role="menuitem"
                  onClick={() => { setUserMenuOpen(false); navigate('/mcp-keys'); }}
                >
                  <Icon d={ICONS.password} />
                  <span>MCP API Key</span>
                </button>
                <button type="button" role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onSignOut(); }}
                >
                  <Icon d={ICONS.signout} />
                  <span>退出登录</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
    </>
  );
}
